import { captureSentryError } from "@/shared/utils/sentry-helpers";
import type { Middleware } from "@reduxjs/toolkit";
import { isRejectedWithValue } from "@reduxjs/toolkit";

/**
 * RTK Query error logger — routes rejected API actions to Sentry with
 * endpoint/method/status tags so the Issues page groups by API call, not by
 * "TypeError: Failed to fetch" string.
 *
 * Filters out noise that isn't actionable:
 *  - 401 / 403 → token expired, user logged out, permission denied. The UI
 *    layer handles these (redirect, re-auth modal).
 *  - status 0 or offline errors → the user lost connectivity. Not a server bug.
 *  - AbortError → the request was cancelled (navigation, component unmount).
 *  - `condition` rejections from RTK Query (not real failures).
 *
 * Everything else gets reported with:
 *   tags: { source: "rtk-query", endpoint, method, status }
 *   extra: { args, url, data }
 *   fingerprint: ["rtk-query", endpoint, status] — groups all 500s on the same
 *     endpoint into one Sentry issue even if the underlying error message
 *     differs between requests.
 */

type FetchBaseQueryErrorLike = {
  status?: number | string;
  data?: unknown;
  error?: string;
};

type RtkRejectedMeta = {
  arg?: {
    endpointName?: string;
    type?: "query" | "mutation";
    originalArgs?: unknown;
  };
  baseQueryMeta?: {
    request?: { url?: string; method?: string };
    response?: { status?: number };
  };
  condition?: boolean;
  aborted?: boolean;
  rejectedWithValue?: boolean;
};

const NOISY_STATUSES = new Set([401, 403]);

const isNoisy = (payload: FetchBaseQueryErrorLike): boolean => {
  const status = payload.status;
  if (typeof status === "number" && NOISY_STATUSES.has(status)) return true;
  // RTK Query emits status: "FETCH_ERROR" for offline/network failures and
  // "PARSING_ERROR" for non-JSON 2xx responses. Both are noisy at scale.
  if (status === "FETCH_ERROR") return true;
  if (payload.error === "AbortError") return true;
  return false;
};

export const sentryRtkErrorLogger: Middleware = () => (next) => (action) => {
  if (!isRejectedWithValue(action)) return next(action);

  const meta = action.meta as RtkRejectedMeta | undefined;

  // Skip RTK Query's `condition: false` short-circuits (not real failures).
  if (meta?.condition === false || meta?.aborted) return next(action);

  const payload = (action.payload ?? {}) as FetchBaseQueryErrorLike;
  if (isNoisy(payload)) return next(action);

  const endpoint = meta?.arg?.endpointName ?? "unknown";
  const method = meta?.baseQueryMeta?.request?.method ?? "unknown";
  const statusRaw = payload.status ?? meta?.baseQueryMeta?.response?.status;
  const status = statusRaw != null ? String(statusRaw) : "unknown";
  const url = meta?.baseQueryMeta?.request?.url;

  const message = `[RTK Query] ${method} ${endpoint} failed (${status})`;
  const error = new Error(message);
  // Preserve any nested error on the payload so Sentry shows the real cause.
  if (typeof payload === "object" && payload && "error" in payload) {
    error.cause = payload.error;
  }

  captureSentryError(error, {
    tags: {
      source: "rtk-query",
      endpoint,
      method,
      status,
    },
    extra: {
      url,
      args: meta?.arg?.originalArgs,
      data: payload.data,
    },
    // Group by endpoint + status so e.g. all 500s on `updateVersion` coalesce
    // into one Sentry issue even when the response body differs.
    fingerprint: ["rtk-query", endpoint, status],
    level: "error",
  });

  return next(action);
};
